import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { CountrySelect } from '@src/components/CountrySelect';
import { ACTIVATE_COUNTRY } from '@src/graphql/queries/country';
import { useAuth } from '@src/hooks/useAuth';
import { useCountryStats } from '@src/hooks/useCountryStats';
import { GlobalPermission } from '@src/utilities/permissions';
import { DataTable, Form, SearchFilterBar, TrackableButton, useAlert, useLocale, } from 'pe-core-frontend';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
export const CountrySchoolStats = () => {
    const { t } = useLocale();
    const { hasPermission } = useAuth();
    const { showAlert } = useAlert();
    const [searchText, setSearchText] = useState('');
    const { stats, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useCountryStats({
        searchText,
    });
    const [activateCountry] = useMutation(ACTIVATE_COUNTRY);
    const [showAdd, setShowAdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const handleValidate = (ac) => {
        const errors = {};
        if (!ac.code) {
            errors.code = t('{field} is required', { values: { field: t('Code') } });
        }
        return errors;
    };
    const handleSave = async (ac) => {
        try {
            setSaving(true);
            const result = await activateCountry({
                variables: {
                    code: ac.code,
                },
            });
            if (result.data?.activateCountry && !result.data.activateCountry.success) {
                return result.data.activateCountry;
            }
            await refetch();
            setShowAdd(false);
            const message = result.data.activateCountry.messages[0];
            const toShow = t(message.value, { values: message.params });
            showAlert(toShow, 'success');
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Error activating country'));
        }
        finally {
            setSaving(false);
        }
    };
    useEffect(() => {
        setPage(1);
    }, [searchText]);
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            // Only reset page if sort actually changed
            if (sort.field !== orderBy || sort.sort !== orderByDir) {
                setOrderBy(sort.field);
                setOrderByDir(sort.sort);
                setPage(1);
            }
        }
        else {
            // Only reset page if we're clearing a sort that existed
            if (orderBy !== null || orderByDir !== null) {
                setOrderBy(null);
                setOrderByDir(null);
                setPage(1);
            }
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Schools'), navLinks: [
                    {
                        title: t('Countries'),
                        path: '/admin/schools/countries',
                        selected: true,
                    },
                    {
                        title: t('Groups'),
                        path: '/admin/schools/accreditations',
                    },
                ], searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: setSearchText, searchPlaceholder: t('Search by Name') }), actions: hasPermission(GlobalPermission.CREATE_ACCREDITATION) && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Add', tag: 'header', variant: 'contained', disabled: saving, onClick: () => {
                        setShowAdd(true);
                    }, children: t('Add') })) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), !loading && stats.length === 0 && (_jsx(Alert, { display: true, hideClose: true, type: 'info', className: 'mt-4 mb-4', children: t('No countries found') })), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: stats, loading: loading, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'firstName', sort: orderByDir }], rowCount: total, columns: [
                                    {
                                        field: 'name',
                                        headerName: t('Name'),
                                        flex: 0.4,
                                        renderCell: ({ value, row }) => (_jsx(Link, { to: `/admin/schools/country/${row.code}`, children: value })),
                                    },
                                    {
                                        field: 'code',
                                        headerName: t('Code'),
                                        width: 100,
                                    },
                                    {
                                        field: 'schoolsCount',
                                        headerName: t('Schools Count'),
                                        width: 100,
                                        align: 'right',
                                    },
                                ] })] }) }) }), showAdd && (_jsx(Form, { fields: [], title: 'Add Country', modal: true, obj: { code: '' }, onValidate: handleValidate, onSave: handleSave, minHeight: 150, onCancel: () => setShowAdd(false), children: ({ draft, setDraft, errors }) => (_jsx(CountrySelect, { label: t('Country'), value: draft.code, onChange: code => setDraft({ code }), errors: errors.code, includeInactive: true })) }))] }));
};
